<template>
  <AIVDialog
    v-model="isVisible"
    title="Support Strategy Acknowledgment"
    width="60%"
    height="85vh"
    class="mobile-friendly-modal support-strategy-modal"
  >
    <div class="ss-modal-content">
      <div class="ss-modal-info">
        <p>
          <strong>Client:</strong> {{ clientName }}
        </p>
        <p>
          <strong>Document:</strong> {{ document ? document.display_name : '' }}
        </p>
      </div>

      <div class="ss-modal-viewer">
        <div v-if="loadingPdf" class="text-center p-4">
          <i class="fa fa-spinner fa-spin"></i> Loading document...
        </div>
        <iframe
          v-else-if="pdfUrl"
          :src="pdfUrl"
          class="ss-pdf-viewer"
        ></iframe>
        <div v-else-if="document && document.is_link" class="text-center p-4">
          <p>This document is an external link.</p>
          <el-button type="primary" @click="openLink">
            <i class="fa fa-external-link-alt mr-1"></i> Open Document
          </el-button>
        </div>
        <div v-else class="text-center p-4">
          <p>Unable to load document preview.</p>
        </div>
      </div>

      <div class="ss-modal-acknowledge">
        <el-checkbox v-model="acknowledged" class="ss-checkbox">
          I have read and understand this Support Strategy
        </el-checkbox>
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleCancel">Cancel</el-button>
        <el-button
          type="primary"
          @click="handleAcknowledge"
          :disabled="!acknowledged"
          :loading="submitting"
        >
          Acknowledge
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import { documentAcknowledgmentsService } from "@/serviceHandlers/documentAcknowledgmentsService";

export default {
  name: "SupportStrategyAcknowledgeModal",
  components: { AIVDialog },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    document: {
      type: Object,
      default: () => null,
    },
    clientName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      acknowledged: false,
      submitting: false,
      loadingPdf: false,
      pdfUrl: null,
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.resetForm();
        this.loadDocument();
      } else {
        this.cleanupPdf();
      }
    },
  },
  beforeDestroy() {
    this.cleanupPdf();
  },
  methods: {
    resetForm() {
      this.acknowledged = false;
      this.submitting = false;
      this.cleanupPdf();
    },

    cleanupPdf() {
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = null;
      }
    },

    async loadDocument() {
      if (!this.document || this.document.is_link) return;

      this.loadingPdf = true;
      try {
        const filename = this.document.link.split("/").pop();
        const apiBaseUrl =
          process.env.VUE_APP_API_URL || window.location.origin;
        const apiUrl = `${apiBaseUrl}/api/pdf/client-document/${encodeURIComponent(filename)}`;
        const token = localStorage.getItem("jwtToken");

        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            Authorization: token,
            Accept: "application/pdf",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load document: ${response.status}`);
        }

        const blob = await response.blob();
        const pdfBlob = new Blob([blob], { type: "application/pdf" });
        this.pdfUrl = URL.createObjectURL(pdfBlob);
      } catch (error) {
        this.$notifyError("Error loading document: " + error.message);
      } finally {
        this.loadingPdf = false;
      }
    },

    openLink() {
      if (this.document && this.document.link) {
        let url = this.document.link;
        if (!/^https?:\/\//i.test(url)) {
          url = `https://${url}`;
        }
        window.open(url, "_blank");
      }
    },

    handleCancel() {
      this.isVisible = false;
      this.resetForm();
    },

    async handleAcknowledge() {
      if (!this.acknowledged || !this.document) return;

      this.submitting = true;
      try {
        await documentAcknowledgmentsService.acknowledge(this.document.id);
        this.$notifySuccess("Support Strategy acknowledged successfully.");
        this.isVisible = false;
        this.$emit("acknowledged");
      } catch (error) {
        const msg =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message;
        this.$notifyError("Error acknowledging document: " + msg);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.ss-modal-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0 1rem;
}

.ss-modal-info {
  margin-bottom: 1rem;
}

.ss-modal-info p {
  margin-bottom: 0.25rem;
  color: #e0e0e0;
}

.ss-modal-info strong {
  color: #fff;
}

.ss-modal-viewer {
  flex: 1;
  min-height: 400px;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  overflow: hidden;
}

.ss-pdf-viewer {
  width: 100%;
  height: 100%;
  min-height: 400px;
  border: none;
}

.ss-modal-acknowledge {
  padding: 1rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.ss-checkbox {
  color: #e0e0e0;
  font-size: 1rem;
}
</style>
